/**
 * Work notes section component for displaying and managing comments and actions
 */

import React, { useRef, useEffect } from 'react';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { Ticket, WorkNote } from '../../types/ticketTypes';
import { sortWorkNotes } from '../utils/ticketUtils';
import { formatTimestamp } from '../utils/formatUtils';
import { WorkNotesHandlers } from '../hooks/useWorkNotes';

export interface WorkNotesSectionProps {
  ticket: Ticket;
  workNotesHandlers: WorkNotesHandlers;
}

export const WorkNotesSection: React.FC<WorkNotesSectionProps> = ({
  ticket,
  workNotesHandlers
}) => {
  const {
    newUpdate,
    setNewUpdate,
    handleAddComment,
    handleAddAction,
    handleToggleActionCompletion
  } = workNotesHandlers;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus the textarea when the component mounts (when a ticket is selected)
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [ticket.id]); // Re-focus when ticket changes

  return (
    <div className="ticket-details-section">
      <div className="section-header">
        <h4>Work Log</h4>
      </div>
      
      {/* Add Updates Form - moved to top */}
      <div className="ticket-updates-content-box">
        <div className="form-group">
          <label htmlFor={`new-update-${ticket.id}`}>Add Comment / Action:</label>
          <textarea
            ref={textareaRef}
            id={`new-update-${ticket.id}`}
            value={newUpdate}
            onChange={(e) => setNewUpdate(e.target.value)}
            placeholder="Enter a comment or action..."
            rows={3}
          />
          <div className="button-group">
            <button 
              type="button"
              className="add-btn comment-btn"
              onClick={() => handleAddComment(ticket)}
              disabled={!newUpdate.trim()}
            >
              Add as Comment
            </button>
            <button 
              type="button"
              className="add-btn action-btn"
              onClick={() => handleAddAction(ticket)}
              disabled={!newUpdate.trim()}
            >
              Add as Action
            </button>
          </div>
        </div>
      </div>
      
      {/* Work Notes Display */}
      <div className="work-notes-container">
        {ticket.workNotes && ticket.workNotes.length > 0 ? (
          sortWorkNotes(ticket.workNotes).map((note: WorkNote) => (
            <div key={note.id} className={`work-note ${note.type} ${note.type === 'action' && note.isCompleted ? 'completed' : ''}`}>
              <div className="worklog-content">
                <div className="work-note-content">
                  {note.content}
                </div>
                <div className="work-note-footer">
                  <span className="work-note-created">
                    {note.type === 'action' 
                      ? (note.isCompleted 
                          ? `Action completed by ${note.author} on ${formatTimestamp(note.timestamp)}`
                          : `Action added by ${note.author} on ${formatTimestamp(note.timestamp)}`)
                      : `Comment added by ${note.author} on ${formatTimestamp(note.timestamp)}`
                    }
                  </span>
                </div>
              </div>
              {note.type === 'action' && (
                <button
                  type="button"
                  className={`action-toggle-btn ${note.isCompleted ? 'completed' : 'pending'}`}
                  onClick={() => handleToggleActionCompletion(ticket, note.id)}
                  title={note.isCompleted ? 'Mark as pending' : 'Mark as completed'}
                >
                  {note.isCompleted ? <CheckCircle /> : <RadioButtonUnchecked />}
                </button>
              )}
            </div>
          ))
        ) : (
          <div className="no-work-notes">
            No work notes added yet. Use the form above to add comments or actions.
          </div>
        )}
      </div>
    </div>
  );
};